<template>
  <form @submit.prevent="handleSubmit">
    <div v-if="showCreditCardForm" ref="paypalForm">
      <t-input
        id="cardHolder"
        v-model="form.name"
        name="nameoncard"
        autocomplete="cc-name"
        autocorrect="off"
        spellcheck="false"
        :label="$t('credit_card.name') + '*'"
        :error="$v.form.name.$dirty && $v.form.name.$error"
      ></t-input>

      <div class="mt-4">
        <label>
          <div class="t-input__label">{{ $t('credit_card.number') }}*</div>
          <div id="card-number" class="form-control"></div>
        </label>
      </div>

      <div class="mt-4">
        <label>
          <div class="t-input__label">{{ $t('credit_card.expiration') }}*</div>
          <div id="expiration-date" class="form-control"></div>
        </label>
      </div>

      <div class="mt-4">
        <label>
          <div class="t-input__label">CVV*</div>
          <div id="cvv" class="form-control"></div>
        </label>
      </div>

      <ul class="mt-4">
        <li
          v-for="(error, index) in formErrors"
          :key="index"
          class="t-input-error-message"
          v-text="error"
        ></li>
      </ul>

      <theme-button
        :loading="loading || isSubmitting"
        :disabled="disabled"
        type="submit"
        class="w-full mt-4 px-3 py-2"
        >{{ $t('base.pay') }}</theme-button
      >
    </div>
    <div v-else class="text-center">
      <t-spinner v-theme-font></t-spinner>
    </div>
  </form>
</template>

<script>
import { PayPal } from 'src/apis'
import {
  isPaypalFieldsOk,
  getPaypalIssueMessage,
  getPaypalIssue
} from 'Utilities/thirdPartyPayment'
import TInput from 'ChatWidget/Common/TInput'
import ThemeButton from 'javascript/Common/ThemeButton'
import { scrollToError } from 'javascript/helpers/dom'
import { required } from 'vuelidate/lib/validators'
import { chatWindowIframeId } from 'javascript/Constants'

export default {
  name: 'PayPalForm',
  components: { ThemeButton, TInput },

  props: {
    loading: Boolean,
    disabled: Boolean,
    expectedBrands: { type: Array, default: () => [] },
    amount: { type: [Number, String], required: true }
  },

  data() {
    return {
      isSubmitting: false,
      portalDisabled: false,
      showCreditCardForm: false,
      paypal_customer_id: null,
      paypalFun: null,
      paypalOk: false,
      formErrors: [],
      form: {
        name: ''
      }
    }
  },

  async created() {
    await this.loadPayPalScript()
  },

  methods: {
    async loadPayPalScript() {
      const data = await PayPal.getPayPalClientToken()
      const { paypal_client_id, paypal_client_token, paypal_customer_id } = data || {}
      this.paypal_customer_id = paypal_customer_id
      await this.loadScript(
        {
          url: `https://www.paypal.com/sdk/js?components=hosted-fields&client-id=${paypal_client_id}&currency=JPY&intent=capture`,
          type: 'paypal',
          clientToken: paypal_client_token
        },
        this.renderPayPalForm
      )
    },

    loadScript({ type, url, clientToken }, callback) {
      const paypalScript = document.createElement('script')
      paypalScript.setAttribute('src', url)
      paypalScript.setAttribute('data-client-token', clientToken)
      paypalScript.onload = callback
      document.getElementById(chatWindowIframeId).contentDocument.head.appendChild(paypalScript)
    },

    tryCheckCardBrand() {
      const type = this.paypalFun?._state?.cards?.[0]?.type?.replace('-', '').toLowerCase()
      return !type || !this.expectedBrands.length || this.expectedBrands.includes(type)
    },

    handleSubmit() {
      this.formErrors = []
      this.$v.form.$touch()
      if (this.$v.$anyError) {
        this.$nextTick(() => scrollToError())
        return
      }

      const invalidClass = '.braintree-hosted-fields-invalid'
      if (document.querySelector(invalidClass)) {
        this.$nextTick(() => scrollToError(invalidClass))
        return
      }

      if (this.isSubmitting || !this.paypalFun) {
        return
      }

      if (!this.tryCheckCardBrand()) {
        this.formErrors = [this.$t('creditCardForm.cardBrandMismatch')]
        this.$nextTick(() => scrollToError('.payment-error'))
        return
      }

      this.isSubmitting = true
      this.paypalFun
        .submit({
          contingencies: ['SCA_WHEN_REQUIRED'],
          vault: false
        })
        .then((_cardData) => {
          console.log(_cardData) // for QA
          this.$emit('submit', {
            card_type: _cardData.card?.brand,
            card_holder: this.form.name,
            paypal_order_id: this.paypal_order_id,
            paypal_customer_id: this.paypal_customer_id
          })
          this.$nextTick(() => {
            this.isSubmitting = false
          })
        })
        .catch((err) => {
          console.log('err', err)
          const errorMsg = getPaypalIssueMessage(err, this)
          const issue = getPaypalIssue(err)

          if ([3203, 3204, 3205].includes(err.code)) {
            // BE handled Error
            this.formErrors = [this.$t(`base.errorCode.${err.code}`)]
          } else if (errorMsg) {
            // FE handled Error
            this.formErrors = [errorMsg]
          } else if (issue) {
            this.formErrors = [this.$t(`triplaPayIssues.Others`)]
            this.$rollbar.error(err, 'Unhandled PayPal Error')
          } else {
            this.formErrors = [this.$t('base.error02')]
            this.$rollbar.error(err, 'Unknown Paypal Payment Error')
          }
          this.$nextTick(() => scrollToError('.payment-error'))
          this.isSubmitting = false
        })
    },

    async renderPayPalForm() {
      this.showCreditCardForm = true
      // Check if card fields are eligible to render for the buyer's country. The card fields are not eligible in all countries where buyers are located.

      const chatbotWindow = document.getElementById(chatWindowIframeId).contentWindow
      if (chatbotWindow.paypal.HostedFields.isEligible() === true) {
        chatbotWindow.paypal.HostedFields.render({
          createOrder: async () => {
            const res = await PayPal.getPaypalOrder({
              amount: Number(this.amount),
              first_name: this.form.name.split(' ')[0],
              last_name: this.form.name.split(' ').slice(1).join(' ')
            })
            this.paypal_order_id = res.paypal_order_id
            return this.paypal_order_id
          },
          styles: {
            input: {
              'font-size': '16px',
              'font-family': 'Muli, -apple-system, "system-ui"'
            }
          },
          fields: {
            number: {
              selector: '#card-number'
            },
            cvv: {
              selector: '#cvv',
              placeholder: 'CVV'
            },
            expirationDate: {
              selector: '#expiration-date',
              placeholder: 'MM/YY'
            }
          }
        }).then((hf) => {
          this.paypalOk = false
          hf.on('validityChange', (evt) => {
            this.paypalOk = isPaypalFieldsOk(evt)
          })
          this.paypalFun = hf
        })
      } else {
        /*
         * Handle experience when
         * Custom Card Fields is not eligible
         */
      }
    }
  },

  validations: {
    form: {
      name: {
        required
      }
    }
  }
}
</script>
